import { sumBy, groupBy, isEmpty, map, compact, concat, castArray, flatMap } from "lodash-es";
import { useCallback, useState } from "react";
import { api } from "../util/api";
import { extractNameFromDataURL } from "../util/helpers";

const mapDataUrlsToFileObjects = (files) => map(compact(files), (f) => ({ name: extractNameFromDataURL(f) }));

export const moveUploadedDocumentsMutator = ([files], state, { changeValue }) => {
  changeValue(state, "supplementalDocuments.files", (value) => {
    return concat(castArray(value), mapDataUrlsToFileObjects(files));
  });

  changeValue(state, "supplementalDocuments.newFiles", () => []);
};

export const setCptCodesMutator = ([cptCodes], state, { changeValue }) => {
  changeValue(state, "testInfo.cptCodes", () => cptCodes);
};

const combineCptCodes = (cptCodes) => {
  const groups = groupBy(cptCodes, ({ id, code }) => `${id}-${code}`);
  return map(groups, (group) => ({
    id: group[0].id,
    code: group[0].code,
    multiplier: sumBy(group, "multiplier"),
  }));
};

const updateSelectedCptCodesFromSelectedTestMappings = (form, selectedMappings) => {
  const formState = form.getState();

  // If the user has provided or previously provided CPT codes, disable the automatic CPT code
  // filling when selected tests change. Otherwise, we're going to load the selected tests from
  // the server and update the CPT code input to reflect the CPT codes from the Test Mapping.
  //
  // The intent here is to provide a preview of what will happen during ingestion on the back-end.
  const cptCodesInputUserModified = formState.touched["testInfo.cptCodes"] || !isEmpty(formState.initialValues.testInfo?.cptCodes);
  const hasCptCodesInput = !isEmpty(formState.values.testInfo?.cptCodes);

  if (cptCodesInputUserModified && hasCptCodesInput) {
    return;
  }

  const cptCodes = combineCptCodes(flatMap(selectedMappings, (m) => m?.cpt_codes));
  form.mutators.setCptCodesMutator(cptCodes);
};

const updateTestIdentifiersFromSelectedTestMappings = (form, selectedMappings) => {
  const joinedIdentifiers = selectedMappings.map((m) => m.identifier).join(",");
  form.change("testInfo.testIdentifier", joinedIdentifiers);
};

export const useSelectedTestUpdateFunction = (form) => {
  const [loading, setLoadingSelectedMappings] = useState(false);
  const [error, setSelectedMappingsError] = useState(null);

  const updateTestInfoFromSelectedTestIds = useCallback(
    async (verifiedTestIds) => {
      try {
        setLoadingSelectedMappings(true);

        const {
          data: { resources: selectedMappings },
        } = await api.get("/tests/test_mappings_by_identifiers.json", { params: { id: verifiedTestIds } });

        updateSelectedCptCodesFromSelectedTestMappings(form, selectedMappings);
        updateTestIdentifiersFromSelectedTestMappings(form, selectedMappings);
      } catch (err) {
        console.error("Error while loading selected mappings", err);
        setSelectedMappingsError(err);
      } finally {
        setLoadingSelectedMappings(false);
      }
    },
    [form]
  );

  return { loading, error, updateTestInfoFromSelectedTestIds };
};
